import { firstValidString, flagInSet } from '../../webmodule-common/other/ui/string-helper-functions';
import {
  Frame,
  FrameAttribute,
  FrameAttributeEditorType,
  FrameAttributeGroup,
  FrameAttributeGroupType,
  FrameBuyInItem
} from '../../webmodule-common/interop/webmodule-interop';
import { html, TemplateResult } from 'lit';
import { interpolateExternal } from '../../localization/global';
import { supplierQuoteItemContentType } from '../quotes/data/supplier-quote-item-content-type';

const typeMap: Record<string, string> = {
  [supplierQuoteItemContentType.CID_FRAM]: 'FRAM',
  [supplierQuoteItemContentType.CID_COMP]: 'COMP',
  [supplierQuoteItemContentType.CID_EXTN]: 'EXTN',
  [supplierQuoteItemContentType.CID_FILL]: 'FILL',
  [supplierQuoteItemContentType.CID_IGU]: 'IGU',
  [supplierQuoteItemContentType.CID_FREEHAND]: 'FREEHAND',
  [supplierQuoteItemContentType.CID_LOUVRE]: 'LOUVRE'
};

export function getBuyInResourceReference(frameBuyInItem: FrameBuyInItem) {
  const type = typeMap[frameBuyInItem.resourceType] ?? 'UNK';
  return `[${type}:${frameBuyInItem.extraDetails?.['SuppCode'] ?? ''}:${frameBuyInItem.code}]`;
  // Example: [FRAM:SUP123:CODE456], [IGU::CODE789]
}

export function getBuyInDetailsTemplate(frameBuyInItem: FrameBuyInItem, heightFirst: boolean | null) {
  const details = frameBuyInItem.extraDetails;
  const empty = ``;

  if (!details) return empty;

  switch (frameBuyInItem.resourceType) {
    case supplierQuoteItemContentType.CID_IGU:
    case supplierQuoteItemContentType.CID_FILL:
      return heightFirst
        ? `${details['HeightDisplayValue'] ?? empty} (H) x ${details['WidthDisplayValue'] ?? empty} (W)`
        : `${details['WidthDisplayValue'] ?? empty} (W) x ${details['HeightDisplayValue'] ?? empty} (H)`;
    case supplierQuoteItemContentType.CID_EXTN:
      return `${details['LengthDisplayValue'] ?? empty}`;
    default:
      return `Unknown Type`;
  }
}

export function getFrameDetailsTemplate(frame: Frame, isReports = true): TemplateResult {
  const attributeTemplate = (a: FrameAttribute) => {
    const visible = (isReports && !a.hideFromReport) || (!isReports && !a.hideFromUI);
    const displayValue = firstValidString(
      flagInSet(a.dataType, FrameAttributeEditorType.Dropdown || FrameAttributeEditorType.Picker)
        ? interpolateExternal(a.displayValue)
        : a.displayValue,
      a.value
    );
    return visible
      ? html` <li>
          <span class="attribute-label">${interpolateExternal(a.description)}</span>
          <span class="attribute-value">${displayValue}</span>
        </li>`
      : html``;
  };

  const groupHasAttributes = (g: FrameAttributeGroup) => {
    return g.attributes.filter(a => (isReports ? !a.hideFromReport : !a.hideFromUI)).length > 0;
  };
  const visibleGroups = (g: FrameAttributeGroup[]) => {
    return isReports
      ? g.filter(g => g.groupType !== FrameAttributeGroupType.Quote && groupHasAttributes(g) && !g.hideFromReport)
      : g.filter(g => g.groupType !== FrameAttributeGroupType.Quote && groupHasAttributes(g) && !g.hideFromUI);
  };
  const groupTemplate = (g: FrameAttributeGroup): TemplateResult => {
    const attributes = g.attributes.filter(a => (isReports && !a.hideFromReport) || (!isReports && !a.hideFromUI));
    if (attributes.length === 0) return html``;
    //we want to fit 3 columns per row??
    return html`
      <div class="col-4 extended-options-block">
        <h5>${interpolateExternal(g.description)}</h5>
        <ul>
          ${attributes.map(a => attributeTemplate(a))}
        </ul>
      </div>
    `;
  };
  const frameDataTemplate = (frameData: Frame) => {
    const stackHasAttributes = (nf: Frame): boolean => {
      const processFrame = (frame: Frame): boolean => {
        return (
          frame.attributeGroups.some(g => groupHasAttributes(g)) || frame.nestedFrames.some(nf => processFrame(nf))
        );
      };
      return processFrame(nf);
    };

    //do not represent frames without useful information
    const frames = frameData.nestedFrames.filter(nf => stackHasAttributes(nf));

    const nestedFrameTemplate = () => {
      if (frames.length === 0) return html``;
      return html`
        <div class="extended-options-level1">
          ${frames.map(
            f =>
              html` <h4 class="extended-options-level-header">${interpolateExternal(f.description)}</h4>
                <div class="extended-options-level2">${frameDataTemplate(f)}</div>`
          )}
        </div>
        </div>`;
    };

    const groups = visibleGroups(frameData.attributeGroups);
    //the layout here is to create a row element and fill it with columns spanning 3 cols per width
    //and then for nested embed and continue
    return html`
      <div class="row">${groups.map(g => groupTemplate(g))}</div>
      ${nestedFrameTemplate()}
    `;
  };

  return html`${frameDataTemplate(frame)}`;
}
